import PictureImage from '../Common/PictureImage';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Canonical from '../../seo/Canonical';
import './Logistics.css';
import '../ShopifyFulfilment/ShopifyFulfilment.css';
import Newsletter from '../Homepage/Newsletter';
import Footer from '../Layouts/Footer';
import FAQs from '../Homepage/FAQs';
import '../Homepage/FAQ.css';
import './ContractPackingFAQ.css';
import { useRevealClass } from '../../hooks/useInViewOnce';

const CONTRACT_HERO_IMAGE = '/logisticsimages/logistics.webp';

const STYLE_ID = 'cp-uk-injected-styles';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes cpFadeUp   { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
    @keyframes cpSlideIn  { from{opacity:0;transform:translateX(-16px)} to{opacity:1;transform:translateX(0)} }

    .cp-ind-card { border-top:3px solid transparent; transition:border-color .25s,transform .25s,box-shadow .25s; }
    .cp-ind-card:hover { border-top-color:#1e3a8a; transform:translateY(-4px); box-shadow:0 12px 36px rgba(30,58,138,.12); }

    .cp-stat-card { transition:background .25s,transform .25s; }
    .cp-stat-card:hover { background:rgba(255,255,255,.1)!important; transform:translateY(-4px); }

    .cp-option-card { transition:border-color .22s,box-shadow .22s,transform .22s; }
    .cp-option-card:hover { border-color:#1e3a8a!important; box-shadow:0 6px 24px rgba(30,58,138,.1); transform:translateY(-2px); }

    .cp-testi-card { transition:box-shadow .25s,transform .25s; position:relative; }
    .cp-testi-card:hover { box-shadow:0 12px 40px rgba(30,58,138,.1); transform:translateY(-4px); }
    .cp-testi-card::before {
      content:'\\201C'; position:absolute; top:20px; right:28px;
      font-size:5rem; line-height:1; color:#e2e8f0;
      font-family:Georgia,serif; pointer-events:none;
    }

    .cp-ind-visible   .cp-ind-card     { animation:cpFadeUp  .5s  ease both; }
    .cp-stats-visible .cp-stat-card    { animation:cpFadeUp  .55s ease both; }
    .cp-opts-visible  .cp-option-card  { animation:cpSlideIn .45s ease both; }
    .cp-testi-visible .cp-testi-card   { animation:cpFadeUp  .55s ease both; }

    @media(max-width:768px){
      .cp-ind-grid,.cp-stats-grid,.cp-opts-grid,.cp-testi-grid{
        grid-template-columns:1fr!important;
      }
    }
  `;
  document.head.appendChild(s);
}

const ContractPacking: React.FC = () => {
  const servicesRef = useRevealClass('logistics-services--visible');
  const industriesRef = useRevealClass('cp-ind-visible');
  const optsRef = useRevealClass('cp-opts-visible');

  const packingServices: Array<{ id: number; title: string; copy: ReactNode }> = [
    {
      id: 1,
      title: 'Custom Kitting & Bundling',
      copy: 'Create bespoke product kits, subscription boxes, promotional packs and multi-SKU bundles tailored to your exact requirements. Every order is assembled accurately and prepared for retail, eCommerce or marketplace distribution.',
    },
    {
      id: 2,
      title: 'Product Labelling & Tagging',
      copy: (
        <>
          Professional product labelling services including FNSKU labels, barcode application, price stickers, care
          labels, expiry labels and custom branding to meet retailer and{' '}
          <Link to="/amazon-fba-prep-services" className="ff-inline-link">
            Amazon FBA prep
          </Link>{' '}
          compliance requirements.
        </>
      ),
    },
    {
      id: 3,
      title: 'Specialist Packaging',
      copy: 'Protect your products with specialist packaging solutions including polybagging, shrink wrapping, gift wrapping, protective packaging and custom box inserts designed to enhance presentation and reduce transit damage.',
    },
    {
      id: 4,
      title: 'Assembly & Repacking',
      copy: 'From simple product assembly to complex repacking projects, our experienced team carries out quality inspections, multi-SKU assembly and final checks before products are dispatched to retailers, fulfilment centres or customers.',
    },
  ];

  const packingCapabilities = [
    {
      name: 'Custom Kitting & Bundling',
      volume: 'Subscription boxes, promo packs & multi-product assemblies',
      accuracy: 'Bespoke to your SOP',
    },
    {
      name: 'Product Labelling',
      volume: 'FNSKU, barcode, price & compliance labels',
      accuracy: 'Amazon & retail ready',
    },
    {
      name: 'Specialist Packaging',
      volume: 'Fragile, premium & high-value goods',
      accuracy: 'Transit-risk reduced',
    },
    {
      name: 'Custom Assembly',
      volume: 'Repacking & QC before storage or dispatch',
      accuracy: 'Spec-led builds',
    },
  ];

  const packingWorkflowSteps: Array<{ id: number; title: string; desc: ReactNode }> = [
    {
      id: 1,
      title: 'Goods Receiving',
      desc: (
        <>
          Your products are received, inspected and logged into our{' '}
          <Link to="/wms-integrations" className="ff-inline-link">
            Warehouse Management System (WMS)
          </Link>
          , ensuring accurate inventory records and full traceability from day one.
        </>
      ),
    },
    {
      id: 2,
      title: 'Kitting & Bundling',
      desc: 'Our experienced team assembles product kits, promotional packs and custom bundles according to your specifications, ensuring consistency and presentation across every order.',
    },
    {
      id: 3,
      title: 'Labelling & Quality Checks',
      desc: 'Products are professionally labelled with FNSKU, barcode, pricing or retailer-specific labels before undergoing thorough quality checks to ensure they meet your packaging requirements.',
    },
    {
      id: 4,
      title: 'Packing & Storage',
      desc: (
        <>
          Completed products are securely packed, stored or prepared for immediate dispatch through our UK fulfilment
          centre, ready for retail distribution,{' '}
          <Link to="/amazon-fba-shipments" className="ff-inline-link">
            Amazon FBA shipments
          </Link>{' '}
          or direct-to-customer delivery.
        </>
      ),
    },
  ];

  const packingFaqs = [
    {
      q: 'What types of contract packing do you offer?',
      a: 'Custom kitting, bundling, FNSKU labelling, fragile packing, product assembly and repacking services.',
    },
    {
      q: 'What is the minimum order quantity?',
      a: 'No minimum — we handle everything from small promo runs to high volume production packing.',
    },
    {
      q: 'How long does contract packing take?',
      a: 'Typical turnaround 2–5 working days. Express service available for urgent requirements.',
    },
    {
      q: 'Can you label products with FNSKU, EAN and pricing information?',
      a: 'Yes. We can apply Amazon-ready FNSKU labels, EAN/GTIN labels, price stickers and care/branding tags as part of the packing process.',
    },
    {
      q: 'Do you handle fragile or specialist packaging requirements?',
      a: 'Yes. We provide protective packing (bubble wrap/foam inserts/air pillows where needed) and we can follow your specialist SOP for fragile items to reduce damage.',
    },
    {
      q: 'Can you kit or assemble multiple SKUs into one bundle or pre-packed set?',
      a: 'Yes. We can build custom bundles/kits and multi-SKU packs, then QC-check each bundle before dispatch or pallet storage.',
    },
  ];

  const industries = [
    {
      icon: '🛒',
      title: 'eCommerce & Direct-to-Consumer (DTC)',
      copy: (
        <>
          Supporting{' '}
          <Link to="/shopify-fulfillment-services" className="ff-inline-link">
            Shopify fulfilment
          </Link>
          , Amazon and marketplace sellers with kitting, bundling, product assembly and retail-ready packaging designed
          for fast, accurate order fulfilment.
        </>
      ),
    },
    {
      icon: '🎁',
      title: 'Gifting & Promotional Packs',
      copy: 'Professionally packed gift sets, corporate hampers, promotional bundles and seasonal campaigns, with premium presentation, custom inserts and gift-wrapping options.',
    },
    {
      icon: '💄',
      title: 'Health, Beauty & Personal Care',
      copy: 'Carefully handling cosmetics, skincare, wellness and personal care products with compliant labelling, protective packaging and thorough quality inspections.',
    },
    {
      icon: '🍫',
      title: 'Food & Beverage',
      copy: 'Contract packing for FMCG products, promotional multipacks, retail displays and seasonal offers, using food-safe handling procedures and accurate product labelling.',
    },
    {
      icon: '🔌',
      title: 'Consumer Electronics',
      copy: 'Secure assembly, protective packaging and barcode or FNSKU labelling for electronics, accessories, cables and multi-component product kits.',
    },
    {
      icon: '📦',
      title: 'Subscription Boxes',
      copy: 'Reliable packing and assembly for monthly subscription boxes, influencer campaigns, product launches and recurring customer deliveries, all prepared to your schedule.',
    },
  ];

  const packagingOptions = [
    {
      icon: '📦',
      name: 'Corrugated Boxes',
      desc: 'Strong single, double and triple-wall corrugated cartons available in a range of sizes to protect products during storage, shipping and fulfilment.',
    },
    {
      icon: '🛍️',
      name: 'Poly Bags',
      desc: 'Clear, recyclable, frosted and branded poly bags with heat sealing and compliant suffocation warnings where required for Amazon FBA and retail distribution.',
    },
    {
      icon: '🎀',
      name: 'Gift Wrapping',
      desc: 'Premium tissue paper, ribbons, gift boxes and branded presentation options for corporate gifting, subscription boxes and seasonal promotions.',
    },
    {
      icon: '🏷️',
      name: 'Custom Labels',
      desc: 'Professional application of FNSKU, EAN, barcode, pricing, compliance and branded labels to meet retailer and marketplace requirements.',
    },
    {
      icon: '🫧',
      name: 'Protective Packaging',
      desc: 'Bubble wrap, kraft paper, foam inserts, air cushions and other protective materials selected to help minimise transit damage and improve product protection.',
    },
    {
      icon: '🗂️',
      name: 'Marketing Inserts & Leaflets',
      desc: 'Include product guides, instruction manuals, thank-you cards, promotional vouchers, warranty information and branded marketing inserts with every order.',
    },
  ];

  return (
    <div className="logistics-page">
      <Canonical baseUrl="https://www.pickpackpro.co.uk" />

      <section className="hero-section fba-page-hero logistics-hero" aria-label="Contract packing services UK">
        <PictureImage
          src={CONTRACT_HERO_IMAGE}
          alt="Contract packing services UK logistics"
          className="fba-page-hero__bg logistics-hero-img"
          width={1920}
          height={1080}
          decoding="async"
          isHero
        />
        <div className="fba-page-hero__overlay" aria-hidden />
        <div className="fba-page-hero__content">
          <div className="sh-left">
            <h1 className="sh-main-title logistics-hero-title">
              <span className="logistics-hero-title-light">Contract Packing</span>{' '}
              <span className="logistics-hero-title-accent">Services UK</span>
            </h1>
            <div className="sh-desc logistics-hero-desc">
              <p>
                Professional contract packing services from our{' '}
                <Link to="/fulfilment-centre-milton-keynes" className="ff-inline-link">
                  Milton Keynes fulfilment centre
                </Link>
                , helping brands prepare products for retail, eCommerce and Amazon. From kitting, bundling and product
                assembly to labelling, repacking and promotional packing, we deliver flexible solutions tailored to your
                business — with seamless integration into{' '}
                <Link to="/multi-channel-fulfillment-services" className="ff-inline-link">
                  multi-channel fulfilment
                </Link>{' '}
                and{' '}
                <Link to="/logistics-services" className="ff-inline-link">
                  e-commerce logistics
                </Link>
                .
              </p>
            </div>
            <div className="sh-cta-wrapper">
              <Link to="/contact-fulfillment-services" className="sh-cta-btn">
                Get Contract Packing Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section ref={servicesRef} className="logistics-services" aria-labelledby="contract-services-title">
        <div className="logistics-services-inner">
          <header className="logistics-services-header">
            <p className="logistics-services-eyebrow">What we handle</p>
            <h2 id="contract-services-title" className="logistics-services-title">
              Our Contract Packing Services
            </h2>
          </header>
          <div className="logistics-tiles-grid" role="list">
            {packingServices.map((s) => (
              <article key={s.id} className="logistics-tile logistics-tile-animate" role="listitem">
                <h3 className="logistics-tile-title">{s.title}</h3>
                <p className="logistics-tile-copy">{s.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '80px 0', background: '#ffffff' }}>
        <div className="container">
          <header style={{ textAlign: 'center', marginBottom: '50px' }}>
            <p className="logistics-services-eyebrow">Global Reach</p>
            <h2 className="logistics-services-title">Packing Capabilities Overview</h2>
          </header>
          <div
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}
          >
            {packingCapabilities.map((cap, i) => (
              <div
                key={i}
                style={{
                  padding: '30px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  textAlign: 'center',
                }}
              >
                <div style={{ color: '#5c82e9', fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '8px' }}>
                  {cap.name}
                </div>
                <div style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '4px' }}>{cap.volume}</div>
                <div style={{ fontSize: '0.85rem', color: '#1e3a8a', fontWeight: '600' }}>{cap.accuracy}</div>
              </div>
            ))}
          </div>
          <p style={{ maxWidth: '720px', margin: '36px auto 0', textAlign: 'center', color: '#64748b', lineHeight: 1.65 }}>
            Pair contract packing with inbound{' '}
            <Link to="/freight-forwarder-uk" className="ff-inline-link">
              freight forwarder UK
            </Link>{' '}
            support, or review{' '}
            <Link to="/pricing-fulfilment-services" className="ff-inline-link">
              fulfilment pricing UK
            </Link>{' '}
            for transparent landed-cost planning.
          </p>
        </div>
      </section>

      <section style={{ padding: '100px 0', background: '#f8fafc' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <header style={{ marginBottom: '60px' }}>
            <p className="logistics-services-eyebrow">Seamless Integration</p>
            <h2 className="logistics-services-title">How Our Contract Packing Process Works</h2>
          </header>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '40px' }}>
            {packingWorkflowSteps.map((step) => (
              <div key={step.id}>
                <div
                  style={{
                    width: '60px',
                    height: '60px',
                    background: '#1e3a8a',
                    color: '#fff',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                  }}
                >
                  {step.id}
                </div>
                <h3 style={{ color: '#0f172a', marginBottom: '12px' }}>{step.title}</h3>
                <p style={{ fontSize: '0.95rem', color: '#64748b' }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '80px 0', background: '#1e3a8a', color: '#fff' }}>
        <div
          className="container"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            alignItems: 'center',
            gap: '50px',
          }}
        >
          <div>
            <p style={{ color: '#fbbf24', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Powered by Tech
            </p>
            <h2 style={{ fontSize: '2.5rem', margin: '15px 0', lineHeight: '1.2' }}>
              Real-Time Visibility Across Every Packing Project
            </h2>
            <p style={{ fontSize: '1.1rem', opacity: '0.9', lineHeight: '1.6' }}>
              Our Mintsoft{' '}
              <Link to="/wms-integrations" className="ff-inline-link" style={{ color: '#fbbf24' }}>
                WMS integrations
              </Link>{' '}
              give you complete visibility throughout the entire contract packing process. From goods receiving and
              inventory management to kitting, labelling, quality inspections and finished stock, every stage is tracked
              in real time. Your dedicated client portal provides up-to-date information on inventory levels, work in
              progress and completed projects.
            </p>
            <ul style={{ listStyle: 'none', padding: 0, marginTop: '20px' }}>
              {[
                'Live project tracking for kitting, bundling and packing activities.',
                'Real-time labelling verification with full product traceability and inventory visibility.',
                'Quality control monitoring throughout every stage of the packing and fulfilment process.',
              ].map((item, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                  <span style={{ color: '#fbbf24', marginRight: '10px' }}>✓</span> {item}
                </li>
              ))}
            </ul>
          </div>
          <div
            style={{
              background: 'rgba(255,255,255,0.1)',
              padding: '40px',
              borderRadius: '20px',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <h3 style={{ marginBottom: '20px' }}>One Provider. Total Control.</h3>
            <p style={{ fontSize: '0.95rem', opacity: '0.8' }}>
              Stop managing multiple spreadsheets and communication gaps between freight agents and 3PL providers —
              packing and fulfilment sit in one UK operation.
            </p>
            <div
              style={{
                marginTop: '25px',
                padding: '15px',
                borderLeft: '4px solid #fbbf24',
                background: 'rgba(0,0,0,0.2)',
              }}
            >
              &quot;Since using Pick Pack Pro contract packing, our product prep accuracy increased by 40% and
              time-to-fulfilment dropped dramatically.&quot;
            </div>
          </div>
        </div>
      </section>

      <section
        ref={industriesRef as React.RefObject<HTMLElement>}
        aria-labelledby="cp-industries-title"
        style={{ padding: '100px 0', background: '#f8fafc' }}
      >
        <div className="container">
          <header style={{ textAlign: 'center', marginBottom: '60px' }}>
            <p className="logistics-services-eyebrow">Sectors we support</p>
            <h2 id="cp-industries-title" className="logistics-services-title">
              Industries We Support
            </h2>
            <p style={{ maxWidth: '640px', margin: '14px auto 0', fontSize: '1rem', color: '#64748b', lineHeight: 1.65 }}>
              From growing eCommerce brands to established retailers, our contract packing services are tailored to meet
              the unique requirements of every industry — whether you need subscription boxes, promotional campaigns, or
              retail-ready products.
            </p>
          </header>
          <div
            className="cp-ind-grid"
            role="list"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '24px' }}
          >
            {industries.map((ind, i) => (
              <article
                key={i}
                role="listitem"
                className="cp-ind-card"
                style={{
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '14px',
                  padding: '32px 28px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px',
                  animationDelay: `${i * 80}ms`,
                }}
              >
                <span style={{ fontSize: '2rem', lineHeight: 1 }} aria-hidden="true">
                  {ind.icon}
                </span>
                <h3 style={{ color: '#0f172a', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>{ind.title}</h3>
                <p style={{ color: '#64748b', fontSize: '0.925rem', lineHeight: 1.65, margin: 0 }}>{ind.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section
        ref={optsRef as React.RefObject<HTMLElement>}
        aria-labelledby="cp-opts-title"
        style={{ padding: '100px 0', background: '#fff' }}
      >
        <div className="container" style={{ maxWidth: '1060px' }}>
          <header style={{ textAlign: 'center', marginBottom: '56px' }}>
            <p className="logistics-services-eyebrow">Full-Service Contract Packing</p>
            <h2 id="cp-opts-title" className="logistics-services-title">
              Packaging Materials &amp; Options
            </h2>
            <p style={{ maxWidth: '640px', margin: '14px auto 0', fontSize: '1rem', color: '#64748b', lineHeight: 1.65 }}>
              We keep a comprehensive range of packaging materials in stock, allowing us to respond quickly to changing
              order volumes without relying on third-party suppliers. Whether you need retail-ready presentation,
              protective packaging, or Amazon FBA-compliant preparation, we recommend the right materials for your products
              and brand.
            </p>
          </header>
          <div
            className="cp-opts-grid"
            role="list"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '20px',
              marginBottom: '44px',
            }}
          >
            {packagingOptions.map((opt, i) => (
              <div
                key={i}
                role="listitem"
                className="cp-option-card"
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '18px',
                  padding: '24px 26px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  animationDelay: `${i * 90}ms`,
                }}
              >
                <div
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '12px',
                    flexShrink: 0,
                    background: '#eff6ff',
                    border: '1px solid #dbeafe',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.4rem',
                  }}
                  aria-hidden="true"
                >
                  {opt.icon}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span style={{ fontSize: '0.975rem', fontWeight: 700, color: '#0f172a' }}>{opt.name}</span>
                  <span style={{ fontSize: '0.85rem', color: '#64748b', lineHeight: 1.55 }}>{opt.desc}</span>
                </div>
              </div>
            ))}
          </div>
          <p style={{ textAlign: 'center', fontSize: '0.925rem', color: '#64748b' }}>
            Need bespoke packaging?{' '}
            <Link to="/contact-fulfillment-services" className="ff-inline-link" style={{ fontWeight: 600 }}>
              Talk to our packing team →
            </Link>
          </p>
        </div>
      </section>

      <FAQs
        className="contract-packing-faqs"
        title="Contract Packing FAQs"
        description="Find answers to common questions about our contract packing services, including kitting and bundling, product assembly, labelling, packaging options, turnaround times, quality control, Amazon FBA preparation, and how we support retailers, manufacturers, and growing eCommerce brands from our Milton Keynes fulfilment centre."
        faqs={packingFaqs.map((f, i) => ({ id: i + 1, question: f.q, answer: f.a }))}
      />

      <Newsletter />
      <Footer />
    </div>
  );
};

export default ContractPacking;
